import useAthenaAPIClient from "../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import { useCallback, useContext, useEffect, useState } from "react";
import { configStore } from "../../../contexts/ConfigContext";
import { LearningSet } from "../../../interfaces/AthenaResources";
import { Loader, LoaderStatus } from "../../../interfaces/Status";
import { sessionStore } from "../../../contexts/SessionContext";
import { ExperimentationSet, Test } from "./type";
import { flattenPool } from "./utils";

const useLearningSets = (teacherId?: string) => {
    const athenaAPIClient = useAthenaAPIClient();
    const {
        config: { apiUrls, mode },
    } = useContext(configStore);
    const {
        session: { statements },
    } = useContext(sessionStore);
    const isTeacher = teacherId !== undefined;

    const [learningSets, setLearningSets] = useState<
        Loader<ExperimentationSet<Test>[]>
    >({
        status: LoaderStatus.Loading,
    });

    const getLearningSets = useCallback(
        async (setIds: string[]) => {
            try {
                const data = await Promise.all<ExperimentationSet<Test>>(
                    setIds.map(async (setId) => {
                        const response = await athenaAPIClient.getLearningSet(
                            setId,
                            {
                                depth: "3",
                                preview: "nested",
                            }
                        );
                        const learningSet = response as LearningSet;
                        const learningSets = response?.learning_sets;

                        let learningItems: Test[];
                        if (learningSets) {
                            learningItems = (learningSet as any)
                                .learning_sets[0].learning_items;
                        } else {
                            learningItems = [];
                        }

                        return {
                            ...learningSet,
                            author_id: "",
                            learning_set_type: "experimentation",
                            status: {
                                status: "sent",
                            },
                            learning_set_context: undefined,
                            learning_sets: undefined,
                            learning_items: learningItems,
                            learning_set_id: setId,
                        };
                    })
                );
                setLearningSets({
                    status: LoaderStatus.Success,
                    payload: data,
                });
            } catch (err) {
                setLearningSets({
                    status: LoaderStatus.Error,
                    err,
                });
            }
        },
        [athenaAPIClient]
    );

    useEffect(() => {
        if (learningSets?.status === LoaderStatus.Success) return;
        if (!mode?.test) {
            setLearningSets({
                status: LoaderStatus.Error,
                err: "Mode test is not defined",
            });
            return;
        }

        const preTestsIds = flattenPool(mode.test.pre) ?? [];
        const postTestsIds = flattenPool(mode.test.post) ?? [];
        const setIds = [...preTestsIds, ...postTestsIds];
        getLearningSets(setIds);
    }, [
        apiUrls.endpoints.analytics,
        athenaAPIClient,
        getLearningSets,
        isTeacher,
        learningSets?.status,
        mode?.test,
        statements,
        teacherId,
    ]);

    return learningSets;
};

export default useLearningSets;
